import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PlacesAutocomplete } from '@/components/ui/PlacesAutocomplete';
import { toast } from 'sonner';
import {
  Briefcase, Upload, Mic, FileText, Code, Loader2, Eye, Download,
  UserPlus, CheckCircle2, AlertTriangle, History, Cpu, Volume2,
  RefreshCw, MapPin, Globe, FileDown, Plus, Search, Edit3,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BASE = `${API_URL}/b2b/mfcwl/admin`;
const ADMIN_ROLES = new Set(['CEO', 'CTO', 'ADMIN', 'SUPER_ADMIN']);

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
const fmtDateOnly = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTimeOnly = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const stripSpaces = (s) => (s || '').replace(/\s+/g, '').toUpperCase();

// Collapse Surepass/MFCWL verbose vehicle names → "Make Model Year"
// "Ford India Pvt Ltd" → "Ford"; "3.2 Endeavour 4 4 At Titanium" → "Endeavour"
function compactMakeModel(v) {
  if (!v) return '';
  const stopWords = /\b(india|pvt|private|ltd|limited|motors?|company|corp|corporation|automobiles?|suzuki|group)\b/gi;
  const make = String(v.make || '')
    .replace(stopWords, '')
    .replace(/\s+/g, ' ').trim()
    .split(' ')[0] || '';
  // Model: take first alphabetic word (drop "3.2", "4 4 At Titanium", etc.)
  const model = String(v.model || '')
    .split(/\s+/)
    .find(tok => /^[A-Za-z][A-Za-z-]{1,}$/.test(tok)) || '';
  return [make, model, v.year].filter(Boolean).join(' ').trim();
}
const STATUS_OPTIONS = ['NEW', 'ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];
const ANALYSIS_STATUS_LABEL = {
  AWAITING_VIDEO: 'Awaiting video',
  QUEUED: 'Queued',
  MIRRORING_OBD: 'Syncing OBD',
  ANALYZING: 'Analyzing…',
  COMPLETED: 'Ready',
  FAILED: 'Failed',
};
const ANALYSIS_STATUS_COLOR = {
  AWAITING_VIDEO: 'bg-gray-100 text-gray-500',
  QUEUED:         'bg-sky-100 text-sky-700',
  MIRRORING_OBD:  'bg-indigo-100 text-indigo-700',
  ANALYZING:      'bg-indigo-100 text-indigo-700',
  COMPLETED:      'bg-emerald-100 text-emerald-700',
  FAILED:         'bg-rose-100 text-rose-700',
};
const STATUS_COLOR = {
  NEW:        'bg-blue-100 text-blue-700',
  ASSIGNED:   'bg-amber-100 text-amber-700',
  IN_PROGRESS:'bg-indigo-100 text-indigo-700',
  COMPLETED:  'bg-emerald-100 text-emerald-700',
  CANCELLED:  'bg-gray-100 text-gray-500',
};

export default function MfcwlDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState('manpower');

  const code = (user?.role_code || user?.role || '').toUpperCase();
  const roles = new Set((user?.roles || []).map(r => (r.code || '').toUpperCase()));
  const isAdmin = ADMIN_ROLES.has(code) || [...roles].some(r => ADMIN_ROLES.has(r));

  if (!user) return <div className="p-8 text-gray-500">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="p-12 text-center" data-testid="mfcwl-access-denied">
        <AlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-3 text-lg font-semibold text-gray-700">Admin access required</h2>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5" data-testid="mfcwl-dashboard">
      <div>
        <div className="flex items-center gap-2">
          <Briefcase className="h-6 w-6 text-indigo-600" />
          <h1 className="text-2xl font-semibold tracking-tight">MFCWL</h1>
          <Badge variant="outline" className="ml-2">Mahindra First Choice Wheels · B2B</Badge>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Partner inspection queue, engine sound AI analyses, service history, reports & invoices.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="mfcwl-tabs">
          <TabsTrigger value="manpower" data-testid="mfcwl-tab-manpower">
            <Cpu className="h-3.5 w-3.5 mr-1.5" /> Manpower + Tech
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="mfcwl-tab-ai">
            <Volume2 className="h-3.5 w-3.5 mr-1.5" /> AI Analysis
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="mfcwl-tab-invoices">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> Invoices
          </TabsTrigger>
        </TabsList>

        <TabsContent value="manpower"><ManpowerTab /></TabsContent>
        <TabsContent value="ai"><AIAnalysisTab /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 1 — Manpower + Tech  (inspections requiring our mechanic)
// ═══════════════════════════════════════════════════════════════════
function ManpowerTab() {
  const [rows, setRows] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editInspection, setEditInspection] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [insp, mech] = await Promise.all([
        axios.get(`${BASE}/inspections`),
        axios.get(`${BASE}/mechanics`),
      ]);
      setRows(insp.data.inspections || []);
      setMechanics(mech.data.mechanics || []);
    } catch { toast.error('Failed to load MFCWL inspections'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <Button size="sm" onClick={() => setNewOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="new-inspection-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> New Inspection
        </Button>
        <Button variant="ghost" size="sm" onClick={load} data-testid="manpower-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-x-auto" data-testid="mfcwl-inspections-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2">Date / Time</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">City</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">Assign to</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Report</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-gray-400">No inspections yet</td></tr>
              ) : rows.map((r) => (
                <Row key={r.wisedrive_id} r={r} mechanics={mechanics}
                     onOpen={() => setOpenId(r.inspection_id)}
                     onEdit={() => setEditInspection(r)}
                     onRefresh={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <InspectionDrawer inspectionId={openId} onClose={() => setOpenId(null)} onRefresh={load} />
      <NewInspectionModal open={newOpen} onClose={() => setNewOpen(false)} onCreated={(id) => {
        setNewOpen(false);
        load();
        setOpenId(id);
      }} />
      <NewInspectionModal
        open={!!editInspection}
        mode="edit"
        initialInspection={editInspection}
        onClose={() => setEditInspection(null)}
        onCreated={() => { setEditInspection(null); load(); }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// + New Inspection — Surepass RC lookup + Google Places + schedule
// ═══════════════════════════════════════════════════════════════════
const TRANSMISSIONS = ['Manual', 'Automatic', 'AMT', 'CVT', 'DCT'];
const FUELS = ['Petrol', 'Diesel', 'CNG', 'Hybrid', 'Electric', 'LPG'];

function initialForm() {
  return {
    customer_name: '', customer_phone: '',
    location_address: '', location_city: '', location_pincode: '',
    location_lat: null, location_lng: null,
    scheduled_at: '',
    vehicle_make: '', vehicle_model: '', vehicle_year: '',
    vehicle_reg_no: '',
    transmission: '', fuel_type: '',
    km_driven: '', has_turbo: false,
    service_tier: 'manpower_tech',
    notes: '',
    assigned_mechanic_id: '',   // optional — assign on save
  };
}

function NewInspectionModal({ open, onClose, onCreated, mode = 'create', initialInspection = null }) {
  const isEdit = mode === 'edit';
  const [rc, setRc] = useState('');
  const [rcLoading, setRcLoading] = useState(false);
  const [rcFetched, setRcFetched] = useState(false);
  const [mechanics, setMechanics] = useState([]);
  const [form, setForm] = useState(initialForm());
  const [submitting, setSubmitting] = useState(false);

  // Hydrate from initialInspection when opening in edit mode
  useEffect(() => {
    if (!open) return;
    if (isEdit && initialInspection) {
      const insp = initialInspection;
      const v = insp.vehicle || {};
      const loc = insp.location || {};
      const cust = insp.customer || {};
      // datetime-local expects "YYYY-MM-DDTHH:mm" in LOCAL time
      let sched = '';
      if (insp.scheduled_at) {
        const d = new Date(insp.scheduled_at);
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        sched = local.toISOString().slice(0, 16);
      }
      setForm({
        customer_name: cust.name || '', customer_phone: cust.phone || '',
        location_address: loc.address || '', location_city: loc.city || '',
        location_pincode: loc.pincode || '',
        location_lat: loc.lat ?? null, location_lng: loc.lng ?? null,
        scheduled_at: sched,
        vehicle_make: v.make || '', vehicle_model: v.model || '',
        vehicle_year: v.year || '', vehicle_reg_no: v.reg_no || '',
        transmission: v.transmission || '', fuel_type: v.fuel_type || '',
        km_driven: v.km_driven ?? '', has_turbo: !!v.has_turbo,
        service_tier: insp.service_tier || 'manpower_tech',
        notes: insp.notes || '',
        assigned_mechanic_id: insp.mechanic?.id || '',
      });
      setRc(v.reg_no || '');
      setRcFetched(true);   // jump straight to full form
    } else if (!isEdit) {
      setForm(initialForm());
      setRc(''); setRcFetched(false);
    }
  }, [open, isEdit, initialInspection]);

  // Load mechanics once the modal opens (filtered client-side by city)
  useEffect(() => {
    if (!open) return;
    axios.get(`${BASE}/mechanics`)
      .then(r => setMechanics(r.data.mechanics || []))
      .catch(() => {});
  }, [open]);

  // Pre-fill scheduled_at with now + 30 min for CREATE only
  useEffect(() => {
    if (open && !isEdit && !form.scheduled_at) {
      const d = new Date(Date.now() + 30 * 60 * 1000);
      d.setSeconds(0, 0);
      d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      setForm(f => ({ ...f, scheduled_at: iso }));
    }
  }, [open, isEdit, form.scheduled_at]);

  const reset = () => {
    setRc(''); setRcFetched(false); setRcLoading(false); setSubmitting(false);
    setForm(initialForm());
  };

  const lookupRc = async () => {
    const clean = rc.replace(/\s+/g, '').toUpperCase();
    if (clean.length < 6) { toast.error('Enter a valid RC number'); return; }
    setRcLoading(true);
    try {
      const r = await axios.get(`${BASE}/rc-lookup/${clean}`);
      const d = r.data;
      setForm(f => ({
        ...f,
        vehicle_reg_no: d.rc_number || clean,
        vehicle_make: d.make || '',
        vehicle_model: d.model || '',
        vehicle_year: d.year || '',
        fuel_type: d.fuel_type || '',
        customer_name: f.customer_name || d.owner_name || '',
      }));
      setRcFetched(true);
      toast.success('Vehicle details fetched');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'RC lookup failed — enter manually');
      setRcFetched(true);  // still let them fill manually
      setForm(f => ({ ...f, vehicle_reg_no: clean }));
    } finally { setRcLoading(false); }
  };

  const onPlaceSelect = (place) => {
    setForm(f => ({
      ...f,
      location_address: place.address || '',
      location_city:    place.city || '',     // always overwrite
      location_lat:     place.latitude ?? null,
      location_lng:     place.longitude ?? null,
      // New city → clear current mechanic selection (city filter may change)
      assigned_mechanic_id: place.city && place.city !== f.location_city ? '' : f.assigned_mechanic_id,
    }));
  };

  const submit = async () => {
    // Required field validation
    const missing = [];
    if (!form.customer_name) missing.push('Customer name');
    if (!form.vehicle_make || !form.vehicle_model || !form.vehicle_year) missing.push('Vehicle details');
    if (!form.transmission) missing.push('Transmission');
    if (!form.fuel_type) missing.push('Fuel type');
    if (!form.km_driven || Number(form.km_driven) < 0) missing.push('Kilometres driven');
    if (!form.location_address) missing.push('Address');
    if (!form.scheduled_at) missing.push('Schedule date/time');
    if (missing.length) { toast.error(`Fill: ${missing.join(', ')}`); return; }

    setSubmitting(true);
    try {
      if (isEdit) {
        // PATCH payload — only send changed fields (backend accepts partial)
        const payload = {
          customer_name:    form.customer_name,
          customer_phone:   form.customer_phone || null,
          location_address: form.location_address,
          location_city:    form.location_city || null,
          location_pincode: form.location_pincode || null,
          location_lat:     form.location_lat,
          location_lng:     form.location_lng,
          scheduled_at:     new Date(form.scheduled_at).toISOString(),
          vehicle_make:     form.vehicle_make,
          vehicle_model:    form.vehicle_model,
          vehicle_year:     Number(form.vehicle_year),
          vehicle_reg_no:   stripSpaces(form.vehicle_reg_no),
          transmission:     form.transmission,
          fuel_type:        form.fuel_type,
          km_driven:        Number(form.km_driven),
          has_turbo:        !!form.has_turbo,
          service_tier:     form.service_tier,
          notes:            form.notes || null,
        };
        const id = initialInspection.inspection_id;
        const r = await axios.patch(`${BASE}/inspections/${id}`, payload);
        // Re-assign mechanic if it changed
        const newMech = form.assigned_mechanic_id;
        const oldMech = initialInspection.mechanic?.id || '';
        if (newMech && newMech !== oldMech) {
          try {
            await axios.post(`${BASE}/inspections/${id}/assign`,
                             { mechanic_id: newMech });
          } catch {
            toast.warning('Inspection updated but mechanic assign failed');
          }
        }
        toast.success(r.data.changed?.length
          ? `Updated · ${r.data.changed.length} field${r.data.changed.length !== 1 ? 's' : ''}`
          : 'Inspection updated');
        onCreated?.(id);
        return;
      }

      const inspection_id = `MFCWL-ADM-${Date.now()}`;
      const payload = {
        inspection_id,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone || null,
        location_address: form.location_address,
        location_city:    form.location_city || null,
        location_pincode: form.location_pincode || null,
        location_lat:     form.location_lat,
        location_lng:     form.location_lng,
        scheduled_at:     new Date(form.scheduled_at).toISOString(),
        vehicle_make:     form.vehicle_make,
        vehicle_model:    form.vehicle_model,
        vehicle_year:     Number(form.vehicle_year),
        vehicle_reg_no:   stripSpaces(form.vehicle_reg_no),
        transmission:     form.transmission,
        fuel_type:        form.fuel_type,
        km_driven:        Number(form.km_driven),
        has_turbo:        !!form.has_turbo,
        service_tier:     form.service_tier,
        notes:            form.notes || null,
      };
      const r = await axios.post(`${BASE}/inspections`, payload);
      if (form.assigned_mechanic_id) {
        try {
          await axios.post(`${BASE}/inspections/${inspection_id}/assign`,
                           { mechanic_id: form.assigned_mechanic_id });
        } catch {
          toast.warning('Inspection created but mechanic assignment failed — use the table dropdown');
        }
      }
      toast.success(`Inspection created · ${r.data.wisedrive_id}`);
      reset();
      onCreated?.(inspection_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEdit ? 'Update failed' : 'Create failed'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && (onClose?.(), reset())}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"
                     data-testid="new-inspection-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit MFCWL Inspection' : 'New MFCWL Inspection'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: RC lookup — hidden in edit mode, RC number is not editable */}
          {!isEdit && (
          <div>
            <Label className="text-xs text-gray-500">Vehicle Registration Number</Label>
            <div className="flex gap-2 mt-1">
              <Input placeholder="e.g. KA03MN4567"
                     value={rc}
                     onChange={e => setRc(e.target.value.toUpperCase())}
                     className="font-mono"
                     onKeyDown={e => e.key === 'Enter' && lookupRc()}
                     data-testid="rc-input" />
              <Button onClick={lookupRc} disabled={rcLoading || !rc.trim()}
                      data-testid="rc-lookup-btn">
                {rcLoading ? <Loader2 className="h-4 w-4 animate-spin" />
                           : <><Search className="h-3.5 w-3.5 mr-1" /> Fetch</>}
              </Button>
            </div>
            <div className="text-[11px] text-gray-400 mt-1">
              Powered by Surepass · auto-fills Make, Model, Year, Fuel type from the RC database
            </div>
          </div>
          )}

          {isEdit && (
            <div className="bg-slate-50 border rounded p-2 text-xs">
              <span className="text-gray-500">Editing:</span>{' '}
              <span className="font-mono font-semibold">{initialInspection?.inspection_id}</span>{' '}
              · <span className="font-mono">{stripSpaces(form.vehicle_reg_no)}</span>
            </div>
          )}

          {rcFetched && (
            <>
              {/* Step 2: Vehicle details (editable, prefilled) */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Make">
                  <Input value={form.vehicle_make}
                         onChange={e => setForm(f => ({ ...f, vehicle_make: e.target.value }))}
                         data-testid="field-make" />
                </Field>
                <Field label="Model">
                  <Input value={form.vehicle_model}
                         onChange={e => setForm(f => ({ ...f, vehicle_model: e.target.value }))}
                         data-testid="field-model" />
                </Field>
                <Field label="Year">
                  <Input type="number" value={form.vehicle_year}
                         onChange={e => setForm(f => ({ ...f, vehicle_year: e.target.value }))}
                         data-testid="field-year" />
                </Field>
                <Field label="Fuel type">
                  <Select value={form.fuel_type} onValueChange={v => setForm(f => ({ ...f, fuel_type: v }))}>
                    <SelectTrigger data-testid="field-fuel"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{FUELS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Transmission *">
                  <Select value={form.transmission} onValueChange={v => setForm(f => ({ ...f, transmission: v }))}>
                    <SelectTrigger data-testid="field-transmission"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{TRANSMISSIONS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Kms driven *">
                  <Input type="number" value={form.km_driven}
                         placeholder="e.g. 48000"
                         onChange={e => setForm(f => ({ ...f, km_driven: e.target.value }))}
                         data-testid="field-km" />
                </Field>
                <Field label="Has Turbo?" full>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={!form.has_turbo}
                             onChange={() => setForm(f => ({ ...f, has_turbo: false }))}
                             data-testid="field-turbo-no" /> No
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="radio" checked={!!form.has_turbo}
                             onChange={() => setForm(f => ({ ...f, has_turbo: true }))}
                             data-testid="field-turbo-yes" /> Yes
                    </label>
                  </div>
                </Field>
              </div>

              {/* Step 3: Customer + Location */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Customer name *">
                  <Input value={form.customer_name}
                         onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                         data-testid="field-customer-name" />
                </Field>
                <Field label="Customer phone">
                  <Input value={form.customer_phone}
                         onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                         data-testid="field-customer-phone" />
                </Field>
                <Field label="Address *" full>
                  <PlacesAutocomplete
                    value={form.location_address}
                    onChange={v => setForm(f => ({ ...f, location_address: v }))}
                    onSelect={onPlaceSelect}
                    placeholder="Search Google Maps…"
                    country="in"
                  />
                  {form.location_city && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      City: <b>{form.location_city}</b>
                      {form.location_lat && ` · ${form.location_lat.toFixed(4)}, ${form.location_lng.toFixed(4)}`}
                    </div>
                  )}
                </Field>
                <Field label="Pincode">
                  <Input value={form.location_pincode}
                         onChange={e => setForm(f => ({ ...f, location_pincode: e.target.value }))}
                         data-testid="field-pincode" />
                </Field>
                <Field label="City">
                  <Input value={form.location_city}
                         onChange={e => setForm(f => ({ ...f, location_city: e.target.value, assigned_mechanic_id: '' }))}
                         placeholder="Auto-filled from address"
                         data-testid="field-city" />
                </Field>
                <Field label="Assign mechanic" full>
                  <MechanicSelectInModal
                    mechanics={mechanics}
                    city={form.location_city}
                    value={form.assigned_mechanic_id}
                    onChange={v => setForm(f => ({ ...f, assigned_mechanic_id: v }))}
                  />
                </Field>
              </div>

              {/* Step 4: Schedule + tier */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                <Field label="Scheduled at *">
                  <Input type="datetime-local"
                         value={form.scheduled_at}
                         min={todayLocalIso()}
                         onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                         data-testid="field-scheduled" />
                  <div className="text-[10px] text-gray-400 mt-0.5">Times shown in your local timezone</div>
                </Field>
                <Field label="Service tier">
                  <Select value={form.service_tier} onValueChange={v => setForm(f => ({ ...f, service_tier: v }))}>
                    <SelectTrigger data-testid="field-tier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manpower_tech">Manpower + Tech (₹600)</SelectItem>
                      <SelectItem value="manpower_tech_history">Manpower + Tech + Service History (₹800)</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Notes" full>
                  <Input value={form.notes}
                         placeholder="Optional — internal notes"
                         onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                </Field>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <Button variant="outline" onClick={() => { onClose?.(); reset(); }}>Cancel</Button>
                <Button onClick={submit} disabled={submitting}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="submit-inspection-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isEdit ? 'Update' : 'Save & Assign'}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <Label className="text-xs text-gray-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Today at 00:00 in the user's local timezone — usable as <input min=...>
function todayLocalIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 16);
}

// Mechanic dropdown filtered by city (picks from the already-loaded list)
function MechanicSelectInModal({ mechanics, city, value, onChange }) {
  const cityNorm = (city || '').toLowerCase().trim();
  const eligible = cityNorm
    ? mechanics.filter(m =>
        (m.inspection_cities || []).some(c => c.toLowerCase() === cityNorm))
    : mechanics;
  const usingFallback = cityNorm && eligible.length === 0;
  const list = usingFallback ? mechanics : eligible;

  return (
    <div className="space-y-1">
      <Select value={value || '__unassigned__'} onValueChange={v => onChange(v === '__unassigned__' ? '' : v)}>
        <SelectTrigger data-testid="field-mechanic">
          <SelectValue placeholder={city ? `Mechanics in ${city}` : 'Pick a location first'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__unassigned__">— Leave unassigned —</SelectItem>
          {list.length === 0
            ? <SelectItem value="__none__" disabled>No mechanics available</SelectItem>
            : list.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} {m.phone ? ` · ${m.phone}` : ''}
                  {(m.inspection_cities || []).length > 0 && (
                    <span className="text-gray-400 ml-1">
                      · {m.inspection_cities.slice(0, 2).join(', ')}
                    </span>
                  )}
                </SelectItem>
              ))}
        </SelectContent>
      </Select>
      {city && usingFallback && (
        <div className="text-[10px] text-amber-600">
          No mechanic mapped to <b>{city}</b> in HR — showing all mechanics as fallback
        </div>
      )}
    </div>
  );
}


function Row({ r, mechanics, onOpen, onEdit, onRefresh }) {
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const changeStatus = async (newStatus) => {
    if (newStatus === r.status) return;
    setUpdatingStatus(true);
    try {
      await axios.post(`${BASE}/inspections/${r.inspection_id}/status`, { status: newStatus });
      toast.success(`Status → ${newStatus}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    } finally { setUpdatingStatus(false); }
  };

  const regNo = stripSpaces(r.vehicle?.reg_no);
  const makeModel = compactMakeModel(r.vehicle);
  const when = r.scheduled_at || r.created_at;

  return (
    <tr className="border-t hover:bg-indigo-50/40" data-testid={`mfcwl-row-${r.inspection_id}`}>
      <td className="px-3 py-2 text-xs text-gray-700 whitespace-nowrap">
        <div className="font-medium">{fmtDateOnly(when)}</div>
        <div className="text-gray-500">{fmtTimeOnly(when)}</div>
      </td>
      <td className="px-3 py-2">
        <div className="font-medium">{r.customer?.name}</div>
        <div className="text-xs text-gray-500">{r.customer?.phone}</div>
      </td>
      <td className="px-3 py-2">
        {r.location?.city ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1 text-sm hover:text-indigo-600 underline-offset-2 hover:underline"
                      data-testid={`city-${r.inspection_id}`}>
                <MapPin className="h-3.5 w-3.5 text-gray-400" />
                {r.location.city}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-xs" side="right" align="start">
              <div className="font-semibold text-gray-800 mb-2">Full address</div>
              <div className="space-y-1 text-gray-700">
                <div>{r.location?.address}</div>
                <div className="text-gray-500">
                  {r.location?.city}
                  {r.location?.pincode && ` · ${r.location.pincode}`}
                </div>
                {(r.location?.lat != null && r.location?.lng != null) && (
                  <a
                    href={`https://maps.google.com/?q=${r.location.lat},${r.location.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Open in Google Maps <MapPin className="h-3 w-3" />
                  </a>
                )}
              </div>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs">
        {regNo ? (
          <>
            <div className="font-mono font-semibold text-sm">{regNo}</div>
            <div className="text-gray-500">{makeModel || '—'}</div>
          </>
        ) : (
          <div className="font-medium">{makeModel || '—'}</div>
        )}
      </td>
      <td className="px-3 py-2">
        {r.mechanic ? (
          <div className="flex items-center gap-2">
            <div className="text-xs min-w-0">
              <div className="font-medium truncate max-w-[160px]">{r.mechanic.name}</div>
              <div className="text-gray-500 truncate max-w-[160px]">{r.mechanic.phone || r.mechanic.email}</div>
            </div>
            <Button size="sm" variant="ghost"
                    className="h-6 w-6 p-0 shrink-0"
                    onClick={onEdit}
                    title="Edit inspection / change mechanic"
                    data-testid={`edit-${r.inspection_id}`}>
              <Edit3 className="h-3 w-3 text-gray-500 hover:text-indigo-600" />
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline"
                  onClick={onEdit}
                  className="h-8 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                  data-testid={`edit-${r.inspection_id}`}>
            <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit details
          </Button>
        )}
      </td>
      <td className="px-3 py-2">
        <Select value={r.status} onValueChange={changeStatus} disabled={updatingStatus}>
          <SelectTrigger
            className={`w-36 h-7 text-[11px] border ${STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-700'}`}
            data-testid={`status-${r.inspection_id}`}
          >
            <SelectValue placeholder={r.status} />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant="ghost" onClick={onOpen} title="Open details"
                data-testid={`open-${r.inspection_id}`}>
          <Eye className="h-3.5 w-3.5" />
        </Button>
      </td>
    </tr>
  );
}

// ── Inspection detail drawer (Report / Upload / Complete) ────────────
function InspectionDrawer({ inspectionId, onClose, onRefresh }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);  // 'svc' | 'sound'

  const load = useCallback(async () => {
    if (!inspectionId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/report.json`);
      setDoc(r.data);
    } catch { toast.error('Fetch failed'); }
    finally { setLoading(false); }
  }, [inspectionId]);

  useEffect(() => { if (inspectionId) load(); }, [inspectionId, load]);

  const uploadServiceHistory = async (file) => {
    setUploading('svc');
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/service-history`, fd);
      toast.success('Service history uploaded + AI-extracted');
      load(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const uploadSoundAi = async (file) => {
    setUploading('sound');
    const fd = new FormData(); fd.append('audio_file', file);
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/sound-ai`, fd);
      toast.success('Engine sound submitted to AI pipeline');
      load(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };

  const markComplete = async () => {
    if (!window.confirm('Mark as COMPLETED? Invoice line will be created.')) return;
    try {
      await axios.post(`${BASE}/inspections/${inspectionId}/complete`, {
        verdict: 'PASS',
      });
      toast.success('Inspection completed');
      load(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const insp = doc?.inspection;

  return (
    <Sheet open={!!inspectionId} onOpenChange={onClose}>
      <SheetContent className="w-[760px] sm:max-w-[760px] overflow-y-auto" data-testid="mfcwl-drawer">
        <SheetHeader>
          <SheetTitle>{insp?.customer?.name} · {insp?.inspection_id}</SheetTitle>
        </SheetHeader>
        {loading || !doc ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="mt-4 space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={STATUS_COLOR[insp?.status] || 'bg-gray-100'}>{insp?.status}</Badge>
              <Badge variant="outline">{insp?.service_tier}</Badge>
              <Badge variant="outline">₹{insp?.unit_price_inr}</Badge>
              {insp?.mechanic?.name && <Badge variant="secondary">Mechanic: {insp.mechanic.name}</Badge>}
            </div>

            {/* Vehicle & Location */}
            <Card><CardContent className="p-3 text-xs grid grid-cols-2 gap-3">
              <div>
                <div className="text-gray-500 mb-1">Vehicle</div>
                <div>{insp?.vehicle?.make} {insp?.vehicle?.model} {insp?.vehicle?.year}</div>
                {insp?.vehicle?.reg_no && <div className="font-mono">{stripSpaces(insp.vehicle.reg_no)}</div>}
                {insp?.vehicle?.vin && <div className="text-gray-500 font-mono">{insp.vehicle.vin}</div>}
              </div>
              <div>
                <div className="text-gray-500 mb-1">Location</div>
                <div>{insp?.location?.address}</div>
                <div className="text-gray-500">{insp?.location?.city} {insp?.location?.pincode}</div>
              </div>
            </CardContent></Card>

            {/* Reports & Delivery */}
            <ReportsDeliveryCard inspectionId={inspectionId} doc={doc} />

            {/* OBD section */}
            <Card><CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-1.5">
                  <Cpu className="h-4 w-4 text-indigo-600" /> OBD Scan
                </div>
                {doc.obd?.summary && (
                  <Badge variant="outline">{doc.obd.summary.total} DTCs · {doc.obd.summary.critical} critical</Badge>
                )}
              </div>
              {doc.obd ? (
                <div className="text-xs space-y-0.5">
                  <div>scan_id: <code>{doc.obd.scan_id}</code></div>
                  <div>verdict: {doc.obd.summary?.ai_verdict || '—'}</div>
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">No OBD scan linked yet. The mechanic will trigger this from the field app using the OBD SDK.</p>
              )}
            </CardContent></Card>

            {/* Sound AI */}
            <Card><CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-1.5">
                  <Volume2 className="h-4 w-4 text-indigo-600" /> Engine Sound AI
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="audio/*,.wav,.mp3,.m4a,.mp4" className="hidden"
                         onChange={e => e.target.files?.[0] && uploadSoundAi(e.target.files[0])}
                         data-testid="upload-sound-input" />
                  <Button size="sm" variant="outline" disabled={uploading === 'sound'} asChild>
                    <span>{uploading === 'sound' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload audio</span>
                  </Button>
                </label>
              </div>
              {doc.sound_ai ? (
                <pre className="text-[10px] bg-gray-50 p-2 rounded max-h-44 overflow-auto">
{JSON.stringify(doc.sound_ai, null, 2).slice(0, 1500)}
                </pre>
              ) : (
                <p className="text-xs text-gray-400 italic">No engine sound submitted yet.</p>
              )}
            </CardContent></Card>

            {/* Service History (only for 800 tier) */}
            {insp?.service_tier === 'manpower_tech_history' && (
              <Card><CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold flex items-center gap-1.5">
                    <History className="h-4 w-4 text-indigo-600" /> Service History
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept=".pdf,image/*" className="hidden"
                           onChange={e => e.target.files?.[0] && uploadServiceHistory(e.target.files[0])}
                           data-testid="upload-svc-input" />
                    <Button size="sm" variant="outline" disabled={uploading === 'svc'} asChild>
                      <span>{uploading === 'svc' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload PDF/image</span>
                    </Button>
                  </label>
                </div>
                {doc.service_history && doc.service_history.length > 0 ? (
                  <div className="space-y-2">
                    {doc.service_history.map(sd => (
                      <div key={sd.id} className="border rounded p-2 text-xs bg-gray-50">
                        <div className="flex justify-between">
                          <span className="font-medium truncate">{sd.original_filename}</span>
                          <Badge variant="outline" className="text-[10px]">
                            {sd.extraction_status === 'done' ? `${sd.extracted_records?.length || 0} records` : sd.extraction_status}
                          </Badge>
                        </div>
                        {sd.extracted_records && sd.extracted_records.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {sd.extracted_records.slice(0, 4).map((rec, i) => (
                              <li key={i} className="text-[11px] text-gray-700">
                                {rec.date} · {rec.odometer_km} km · {rec.service_type}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">No service history uploaded yet. Drop any job-card PDF or photo — we'll AI-extract it.</p>
                )}
              </CardContent></Card>
            )}

            {/* Complete */}
            {insp?.status !== 'COMPLETED' && (
              <div className="flex justify-end pt-2">
                <Button onClick={markComplete} data-testid="mark-complete-btn">
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Mark as COMPLETED
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════
// REPORTS & DELIVERY — consolidated Web / PDF / JSON + audit trail
// ═══════════════════════════════════════════════════════════════════
function ReportsDeliveryCard({ inspectionId, doc }) {
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [rawOpen, setRawOpen] = useState(false);

  const analysisStatus = doc?.inspection?.analysis_status || doc?.analysis_status || 'AWAITING_VIDEO';
  const ready = analysisStatus === 'COMPLETED';
  const combined = doc;

  const issueLink = useCallback(async () => {
    setLinkLoading(true);
    try {
      const r = await axios.post(`${BASE}/inspections/${inspectionId}/public-link`, { ttl_days: 7 });
      setLink(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Link generation failed');
    } finally { setLinkLoading(false); }
  }, [inspectionId]);

  useEffect(() => {
    if (ready && !link && !linkLoading) { issueLink(); }
  }, [ready, link, linkLoading, issueLink]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${BASE}/inspections/${inspectionId}/audit`);
      setAudit(r.data);
    } catch {
      toast.error('Audit trail fetch failed');
    } finally { setAuditLoading(false); }
  }, [inspectionId]);
  useEffect(() => { if (inspectionId) loadAudit(); }, [inspectionId, loadAudit]);

  const dlPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/inspections/${inspectionId}/report.pdf`,
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      const safeReg = stripSpaces(doc?.inspection?.vehicle?.reg_no) || inspectionId;
      a.href = url; a.download = `MFCWL_Inspection_${safeReg}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF generation failed');
    } finally { setPdfLoading(false); }
  };

  const dlJson = () => {
    const blob = new Blob([JSON.stringify(combined, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${inspectionId}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (txt, label = 'Copied') => {
    try { navigator.clipboard.writeText(txt); toast.success(label); } catch { /* ignore */ }
  };

  return (
    <Card className="border border-indigo-100" data-testid="reports-delivery-card">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold flex items-center gap-1.5">
            <FileText className="h-4 w-4 text-indigo-600" /> Reports & Delivery
          </div>
          <Badge className={`text-[10px] ${ANALYSIS_STATUS_COLOR[analysisStatus] || 'bg-gray-100 text-gray-500'}`}>
            {ANALYSIS_STATUS_LABEL[analysisStatus] || analysisStatus}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {/* WEB */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <Globe className="h-3.5 w-3.5 text-indigo-600" /> Web Report
            </div>
            {!ready ? (
              <div className="text-[11px] text-gray-400">Available after AI analysis completes.</div>
            ) : link ? (
              <div className="space-y-1">
                <a href={link.web_url} target="_blank" rel="noreferrer"
                   className="text-[11px] text-indigo-600 hover:underline truncate block font-mono"
                   data-testid="drawer-web-link">
                  {link.web_url.replace(/^https?:\/\//, '').slice(0, 42)}…
                </a>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2"
                          onClick={() => window.open(link.web_url, '_blank', 'noopener')}
                          data-testid="drawer-open-web">
                    Open new tab
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2"
                          onClick={() => copyToClipboard(link.web_url, 'Web URL copied')}>
                    Copy
                  </Button>
                </div>
                <div className="text-[9px] text-gray-400">Expires in {link.ttl_days} days</div>
              </div>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={linkLoading}
                      onClick={issueLink} data-testid="drawer-issue-link">
                {linkLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate link'}
              </Button>
            )}
          </div>

          {/* PDF */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <FileDown className="h-3.5 w-3.5 text-rose-600" /> MFCWL PDF
            </div>
            {!ready ? (
              <div className="text-[11px] text-gray-400">Available after AI analysis completes.</div>
            ) : (
              <div className="space-y-1">
                <div className="text-[10px] text-gray-500">MFCWL-branded inspection report</div>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={dlPdf} disabled={pdfLoading}
                        data-testid="drawer-download-pdf">
                  {pdfLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                              : <Download className="h-3 w-3 mr-1" />} Download PDF
                </Button>
                {link?.pdf_url && (
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 ml-1"
                          onClick={() => copyToClipboard(link.pdf_url, 'PDF URL copied')}>
                    Copy tokenised URL
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* JSON */}
          <div className="border rounded-md p-2 bg-white">
            <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-1">
              <Code className="h-3.5 w-3.5 text-slate-700" /> JSON
            </div>
            <div className="space-y-1">
              <div className="text-[10px] text-gray-500">
                Combined inspection + OBD + AI payload
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        onClick={dlJson} data-testid="drawer-download-json">
                  <Download className="h-3 w-3 mr-1" /> Download
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                        onClick={() => setRawOpen(v => !v)}
                        data-testid="drawer-toggle-json">
                  {rawOpen ? 'Hide' : 'Preview'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {rawOpen && (
          <pre className="text-[10px] bg-slate-900 text-slate-100 p-2 rounded max-h-60 overflow-auto font-mono">
{JSON.stringify(combined, null, 2).slice(0, 4000)}
          </pre>
        )}

        <div className="border-t pt-2">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-medium text-gray-700">MFCWL Audit Trail</div>
            <Button size="sm" variant="ghost" className="h-6 text-[10px]"
                    onClick={loadAudit} disabled={auditLoading}
                    data-testid="drawer-refresh-audit">
              <RefreshCw className={`h-3 w-3 ${auditLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          {auditLoading && !audit ? (
            <div className="text-[11px] text-gray-400 italic">Loading…</div>
          ) : !audit || audit.count === 0 ? (
            <div className="text-[11px] text-gray-400 italic">No audit events yet.</div>
          ) : (
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {audit.events.map((e, i) => <AuditRow key={e.id || i} e={e} />)}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const AUDIT_LABELS = {
  'mfcwl.partner.inspection.create':   { label: 'Inspection created by MFCWL',   icon: 'IN',  color: 'text-emerald-700' },
  'mfcwl.partner.inspection.poll':     { label: 'MFCWL polled inspection',       icon: 'PL',  color: 'text-slate-600' },
  'mfcwl.partner.engine_video.upload': { label: 'MFCWL uploaded engine video',   icon: 'EV',  color: 'text-indigo-700' },
  'mfcwl.partner.report.json.fetch':   { label: 'MFCWL pulled JSON report',      icon: 'JS',  color: 'text-blue-700' },
  'mfcwl.partner.report.pdf.fetch':    { label: 'MFCWL pulled PDF report',       icon: 'PD',  color: 'text-rose-700' },
  'mfcwl.inspection.assign':           { label: 'Mechanic assigned',             icon: 'AS',  color: 'text-indigo-700' },
  'mfcwl.inspection.status':           { label: 'Status changed',                icon: 'ST',  color: 'text-amber-700' },
  'mfcwl.engine_video.upload':         { label: 'Engine video uploaded (admin)', icon: 'EV',  color: 'text-indigo-700' },
};

function AuditRow({ e }) {
  const [open, setOpen] = useState(false);
  const meta = AUDIT_LABELS[e.action] || { label: e.action, icon: '·', color: 'text-gray-600' };
  const actor = e.actor_type === 'partner'
    ? (e.actor_name || e.actor_id || 'partner')
    : (e.actor_email || 'admin');
  return (
    <div className="border rounded-sm bg-slate-50 p-1.5 text-[11px]">
      <button type="button" className="w-full text-left flex items-start gap-2"
              onClick={() => setOpen(v => !v)}>
        <span className="shrink-0 text-[9px] font-mono bg-slate-200 text-slate-700 rounded px-1 py-0.5">{meta.icon}</span>
        <div className="flex-1 min-w-0">
          <div className={`font-medium ${meta.color}`}>
            {meta.label}
            {e.http_status && (
              <span className={`ml-1.5 text-[9px] px-1 rounded ${
                e.http_status >= 400 ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'
              }`}>
                HTTP {e.http_status}
              </span>
            )}
          </div>
          <div className="text-[10px] text-gray-500 truncate">
            {actor} · {new Date(e.ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        </div>
        <span className="text-gray-400 text-[10px]">{open ? '▲' : '▼'}</span>
      </button>
      {open && e.details && Object.keys(e.details).length > 0 && (
        <pre className="mt-1 text-[9px] bg-white p-1.5 rounded border font-mono overflow-auto max-h-28">
{JSON.stringify(e.details, null, 2)}
        </pre>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// TAB 2 — AI Analysis (engine video → Sound AI only)
// ═══════════════════════════════════════════════════════════════════
function AIAnalysisTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/engine-analyses`);
      setRows(r.data.analyses || []);
    } catch { toast.error('Failed to load analyses'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-between text-xs text-gray-500">
        <div>{rows.length} engine analyses · MFCWL uploads via <code>POST /api/b2b/mfcwl/engine-analyses</code></div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="mfcwl-analyses-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Received</th>
                <th className="text-left px-3 py-2">Inspection ID</th>
                <th className="text-left px-3 py-2">Vehicle</th>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No engine analyses yet</td></tr>
              ) : rows.map(a => (
                <tr key={a.id} className="border-t hover:bg-indigo-50/40 cursor-pointer"
                    onClick={() => setOpenId(a.id)} data-testid={`analysis-row-${a.id}`}>
                  <td className="px-3 py-2 text-xs text-gray-600">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{a.inspection_id}</td>
                  <td className="px-3 py-2 text-xs">{a.vehicle?.make} {a.vehicle?.model} {a.vehicle?.year}</td>
                  <td className="px-3 py-2 text-xs">{a.filename}</td>
                  <td className="px-3 py-2">
                    <Badge className={a.status === 'completed' ? STATUS_COLOR.COMPLETED : STATUS_COLOR.IN_PROGRESS}>
                      {a.status}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right"><Eye className="h-4 w-4 text-gray-400 inline" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AnalysisDrawer analysisId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function AnalysisDrawer({ analysisId, onClose }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!analysisId) return;
    setLoading(true);
    axios.get(`${BASE}/engine-analyses/${analysisId}`)
      .then(r => setDoc(r.data))
      .catch(() => toast.error('Load failed'))
      .finally(() => setLoading(false));
  }, [analysisId]);

  return (
    <Sheet open={!!analysisId} onOpenChange={onClose}>
      <SheetContent className="w-[720px] sm:max-w-[720px] overflow-y-auto" data-testid="mfcwl-analysis-drawer">
        <SheetHeader>
          <SheetTitle className="font-mono">{analysisId}</SheetTitle>
        </SheetHeader>
        {loading || !doc ? (
          <div className="py-16 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="mt-4 text-sm space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge>{doc.status}</Badge>
              <Badge variant="outline">inspection: {doc.inspection_id}</Badge>
              <Badge variant="outline">₹{doc.unit_price_inr}</Badge>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => {
                const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `${analysisId}.json`; a.click();
                URL.revokeObjectURL(url);
              }}>
                <Download className="h-3.5 w-3.5 mr-1" /> JSON
              </Button>
            </div>
            <Card><CardContent className="p-3 text-xs">
              <div className="font-semibold text-sm mb-1">Vehicle</div>
              <pre className="bg-gray-50 p-2 rounded text-[11px]">{JSON.stringify(doc.vehicle, null, 2)}</pre>
            </CardContent></Card>
            {doc.result ? (
              <Card><CardContent className="p-3">
                <div className="font-semibold text-sm mb-1">Sound AI result</div>
                <pre className="text-[11px] bg-gray-50 p-2 rounded max-h-96 overflow-auto">
{JSON.stringify(doc.result, null, 2)}
                </pre>
              </CardContent></Card>
            ) : (
              <p className="text-xs text-gray-400 italic">Analysis still processing. Refresh the panel in ~30 s.</p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TAB 3 — Invoices (monthly rollup)
// ═══════════════════════════════════════════════════════════════════
function InvoicesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/invoices`);
      setRows(r.data.invoices || []);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mt-4 space-y-3">
      <div className="flex justify-between text-xs text-gray-500">
        <div>{rows.length} monthly invoice{rows.length !== 1 ? 's' : ''}</div>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-gray-500"><Loader2 className="inline h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="border rounded-lg overflow-hidden" data-testid="mfcwl-invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Month</th>
                <th className="text-right px-3 py-2">Inspections</th>
                <th className="text-left px-3 py-2">Tiers used</th>
                <th className="text-right px-3 py-2">Total (INR)</th>
                <th className="text-left px-3 py-2">Latest</th>
                <th className="text-right px-3 py-2">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">No billable inspections yet</td></tr>
              ) : rows.map(r => (
                <tr key={r.month} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.month}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-2 text-xs">{(r.tiers || []).join(', ')}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">₹ {Number(r.total_inr).toLocaleString('en-IN')}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{fmtDate(r.latest)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" disabled title="PDF generation wired next iteration">
                      <FileText className="h-3.5 w-3.5 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-gray-400 italic">
        PDF invoice auto-generation is scheduled for month-end. For now, row totals reflect every completed inspection's tier price.
      </p>
    </div>
  );
}
